import type { PermalinkIdentityFilters } from '@/features/fanfiction-grid/utils/does-filter-state-match-permalink-identity';
import { getFandomPermalinkBasePath } from '@/features/seo-listing/consts';
import { buildFromUrlFromFilters } from '@/features/seo-listing/utils/build-permalink.util';

export type SeoDisabledFallbackFilters = {
  selectedFandoms: string[];
  selectedTags: string[];
  selectedUniverses: string[];
  selectedStatuses: string[];
  selectedRatings: string[];
  selectedCharacters: string[];
  selectedPairingCharacters: string[];
};

const buildQueryFromFilters = (
  filters: SeoDisabledFallbackFilters,
  options: { omitFandoms?: boolean } = {},
): string => {
  const params = new URLSearchParams();
  if (!options.omitFandoms && filters.selectedFandoms.length > 0) {
    params.set('fandoms', filters.selectedFandoms.join(','));
  }
  if (filters.selectedTags.length > 0) params.set('tags', filters.selectedTags.join(','));
  if (filters.selectedUniverses.length > 0) {
    params.set('universes', filters.selectedUniverses.join(','));
  }
  if (filters.selectedStatuses.length > 0) {
    params.set('statuses', filters.selectedStatuses.join(','));
  }
  if (filters.selectedRatings.length > 0) {
    params.set('ratings', filters.selectedRatings.join(','));
  }
  if (filters.selectedCharacters.length > 0) {
    params.set('characters', filters.selectedCharacters.join(','));
  }
  if (filters.selectedPairingCharacters.length > 0) {
    params.set('pairingCharacters', filters.selectedPairingCharacters.join(','));
  }
  return params.toString();
};

/**
 * When SEO redirect is 404 but the fandom facet still matches the enabled
 * permalink base, keep `/fanfics/fandoms/{slug}` and put extras in GET
 * (DEV-490 variants 2/5 — e.g. multi characters on a character page).
 */
export const buildSeoDisabledFallbackUrl = (
  basePath: '/fanfics' | '/originals',
  pathname: string,
  filters: SeoDisabledFallbackFilters,
  expectedIdentity?: PermalinkIdentityFilters | null,
): string => {
  const fandomBase = getFandomPermalinkBasePath(pathname);
  const fandomStillMatches =
    Boolean(fandomBase) &&
    filters.selectedFandoms.length === 1 &&
    expectedIdentity?.selectedFandoms?.length === 1 &&
    filters.selectedFandoms[0] === expectedIdentity.selectedFandoms[0];

  if (fandomStillMatches && fandomBase) {
    const query = buildQueryFromFilters(filters, { omitFandoms: true });
    return query ? `${fandomBase}?${query}` : fandomBase;
  }

  return buildFromUrlFromFilters(basePath, {
    fandoms: filters.selectedFandoms,
    tags: filters.selectedTags,
    universes: filters.selectedUniverses,
    statuses: filters.selectedStatuses,
    ratings: filters.selectedRatings,
    characters: filters.selectedCharacters,
    pairingCharacters: filters.selectedPairingCharacters,
  });
};
